"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, PhoneCall, Wrench, ShieldCheck, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const faqList = [{
  id: "faq-1",
  question: "Why is my faucet constantly dripping, and how much water is wasted?",
  answer: "A steady drip is commonly caused by worn-out O-rings, corroded valve seats, or failed internal cartridges. Even a moderate leak can waste over 3,000 gallons of water per year and eventually damage your sink basin and vanity base."
}, {
  id: "faq-2",
  question: "Can a cracked sink or loose basin be repaired, or does it need full replacement?",
  answer: "Hairline surface scratches or loose undermount clips can frequently be repaired or re-anchored. However, deep structural cracks in porcelain, cast iron, or acrylic basins present leak risks and typically require fixture replacement to protect subfloors and cabinets."
}, {
  id: "faq-3",
  question: "What causes sudden low water pressure from a kitchen or bathroom faucet?",
  answer: "The most common culprit is a mineral-clogged aerator or cartridge screen. In other cases, corroded supply flex lines, a partially closed shutoff valve, or an undetected pipe leak behind the wall can restrict overall water delivery."
}, {
  id: "faq-4",
  question: "Is water leaking under my sink cabinet considered an emergency?",
  answer: "Yes. Active leaks beneath sinks rapidly warp cabinetry, rot subflooring, and foster toxic mold growth within 24 to 48 hours. If shutoff valves fail to turn off or continuous pooling occurs, shut your main water line and contact our emergency dispatch."
}, {
  id: "faq-5",
  question: "How quickly can Jonesboro Emergency Plumber arrive for urgent repairs?",
  answer: "Our mobile plumbing units operate 24/7 across Jonesboro and surrounding Clayton County communities. We prioritize active leak emergencies and typically arrive within 45 to 60 minutes equipped with standard replacement parts."
}];
const highlights = [{
  title: "24/7 Rapid Response",
  subtitle: "Always on standby across Jonesboro, GA"
}, {
  title: "Upfront Estimates",
  subtitle: "Transparent pricing before any work starts"
}];
export default function FaucetFaq() {
  return <section id="faucetsinkfaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 inline-flex items-center gap-1.5 px-3 py-1 text-xs uppercase tracking-wider font-semibold border-primary/20 text-primary bg-primary/5">
            <HelpCircle className="size-3.5" />
            Common Inquiries
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Faucet & Sink Repair FAQs
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            Clear answers to frequent questions about leaking faucets, damaged sink basins, low pressure, and urgent repairs in Jonesboro, GA.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* FAQ Accordion Column */}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="faq-1" className="w-full space-y-4">
              {faqList.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="bg-card border border-border rounded-xl px-5 py-1 shadow-xs transition-colors hover:border-primary/40 data-[state=open]:border-primary/50 data-[state=open]:shadow-sm">
                  <AccordionTrigger className="text-base sm:text-lg font-semibold text-card-foreground hover:no-underline py-4 text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {/* Emergency Sidebar Column */}
          <div className="lg:col-span-5 space-y-6">
            <Card className="border border-border bg-card shadow-sm rounded-2xl overflow-hidden">
              <CardHeader className="bg-primary/5 border-b border-border p-6">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                    <Wrench className="size-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg sm:text-xl font-default font-bold text-card-foreground">
                      Need Urgent Plumbing Help?
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                      Jonesboro Emergency Plumber is ready 24/7.
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Don't let a steady leak or damaged sink cause costly water damage to your cabinetry and floor. Speak immediately with a local licensed plumber.
                </p>

                <div className="space-y-3 pt-2">
                  {highlights.map((highlight, index) => <div key={index} data-index={index} className="flex items-start gap-3 p-3 rounded-lg bg-background border border-border/60">
                      {index === 0 ? <Clock className="size-5 text-primary shrink-0 mt-0.5" /> : <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />}
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">
                          {highlight.title}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {highlight.subtitle}
                        </p>
                      </div>
                    </div>)}
                </div>

                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="w-full font-semibold shadow-sm">
                    <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>Call (470) 847-9076</span>
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full font-semibold">
                    <Link to="/contact/">Request Service</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}